import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { ConditionalContext } from "./ConditionalContext";
import { ConditionalControls } from "./ConditionalControls";
import { ConditionalDisplay } from "./ConditionalDisplay";
import { ConditionalEditor } from "./ConditionalEditor";
import { ToggleConditional } from "./ToggleConditional";
import { ConditionedAttribute, buildConditionedPath } from "./HelpersConditional";
import { useTreeModifyContext } from "../TreeDataContext";
import { usePathContext } from "../PathInfo";
import { clamp } from "../MathHelpers";

interface ConditionalMiddlewareProps {
  originalComponent: Function | Function[];
  propertyName: string;
  conditions: ConditionedAttribute[];
  renderDefaultControls?: boolean;
  spreadControls?: boolean;
  conditionalResultKeywordOverride?: string;
  renderCallback?: Function;
}

function ConditionalMiddleware(props: ConditionalMiddlewareProps) {
  const { saveChangesAtPath, removeAtPath } = useTreeModifyContext();
  const { path } = usePathContext();

  const [selectedResult, setSelectedResult] = useState(0);
  const [isEditingCondition, setEditingCondition] = useState(false);

  const isConditional =
    props.conditions.length > 1 || props.conditions.some((condition) => condition.condition !== undefined);

  const selectedValue = useMemo(() => {
    if (!props.conditions[selectedResult]) {
      return null;
    }
    return props.conditions[selectedResult].result;
  }, [props.conditions, selectedResult]);

  const childComponent = useCallback(() => {
    let propName = buildConditionedPath(
      selectedResult,
      props.propertyName,
      props.conditions,
      props.conditionalResultKeywordOverride || "result",
    );

    if (props.originalComponent instanceof Array) {
      return props.originalComponent.map((component) => component(selectedValue, propName));
    }
    return props.originalComponent(selectedValue, propName);
  }, [props, selectedResult, selectedValue]);

  const addConditionalResult = useCallback(
    (addKey?: string, addValue?: object) => {
      const newCondition = Object.assign({}, props.conditions[0]);
      newCondition.condition = {};

      if (addKey !== undefined && addValue !== undefined) {
        newCondition.condition = { [addKey]: addValue };
      }

      const conditionPath = path.slice();
      conditionPath.push("__cond_" + props.propertyName);
      let targetIndex = props.conditions.length;
      if (props.conditions[0].condition === undefined) {
        targetIndex--;
      }
      conditionPath.push(targetIndex.toString());
      saveChangesAtPath(newCondition, conditionPath);
      setSelectedResult(props.conditions.length);
    },
    [path, props.conditions, props.propertyName, saveChangesAtPath],
  );

  const removeResult = useCallback(() => {
    if (props.conditions[0].condition === undefined && selectedResult === 0) {
      return;
    }

    const conditionPath = path.slice();
    conditionPath.push("__cond_" + props.propertyName);

    if (props.conditions.length !== 1) {
      if (props.conditions[0].condition !== undefined) {
        conditionPath.push(selectedResult.toString());
      } else {
        conditionPath.push((selectedResult - 1).toString());
      }
    }
    removeAtPath(conditionPath);
    setSelectedResult(clamp(selectedResult - 1, 0, props.conditions.length - 1));
  }, [path, props.conditions, props.propertyName, removeAtPath, selectedResult]);

  const canRemoveCurrentResult = useCallback(() => {
    if (props.conditions[0].condition === undefined && selectedResult === 0) {
      return false;
    }
    return true;
  }, [props.conditions, selectedResult]);

  const conditionalContext = useMemo(
    () => ({
      conditions: props.conditions,
      updateValues: (newValue: any) => {
        console.log("updating", newValue, selectedResult);
      },
      selectedCondition: selectedResult,
      changeSelectedCondition: (newValue: number) => setSelectedResult(newValue),
      unconditionedName: props.propertyName,
      addConditionalResult: addConditionalResult,
    }),
    [addConditionalResult, props.conditions, props.propertyName, selectedResult],
  );
  if (!props.conditions[selectedResult]) {
    setSelectedResult(0);
    return <div>Error: Selected result not found</div>;
  }
  if (props.conditions.length == 0) {
    return <div>Error: No conditions</div>;
  }

  return (
    <>
      <ConditionalContext.Provider value={conditionalContext}>
        {childComponent()}

        <Box className={"conditional-controls game-piece-property-conditional"}>
          <ToggleConditional isConditional={isConditional} renderDefaultControls={props.renderDefaultControls} />
          <Box className="horizontal-flex flex-wrap" justifyContent={props.spreadControls ? "space-between" : "unset"}>
            <ConditionalControls
              isConditional={isConditional}
              isEditingCondition={isEditingCondition}
              setEditingCondition={setEditingCondition}
              removeResult={removeResult}
              canRemoveResult={canRemoveCurrentResult()}
            />
          </Box>
          {isConditional && <ConditionalDisplay value={props.conditions[selectedResult].condition} />}
          {isEditingCondition && (
            <ConditionalEditor
              currentCondition={props.conditions[selectedResult].condition}
              setIsEditingCondition={setEditingCondition}
            />
          )}
        </Box>
      </ConditionalContext.Provider>
    </>
  );
}

export default ConditionalMiddleware;
